import React, { useMemo, useState } from 'react';
import InitialAvatar from '../InitialAvatar';
import { describeCell } from '../../utils/presenceKeys';
import './PresenceBar.css';

const MAX_VISIBLE = 5;

/**
 * Overlapping avatar stack of everyone currently on this page.
 * Feed it the `here`/`cells` roster from usePresence.
 */
const PresenceBar = ({
    here = [],
    cells = {},
    connected = false,
    me = '',
    size = 26,
    label = 'Viewing now',
}) => {
    const [open, setOpen] = useState(false);

    // What each person currently has open, so the popover can say "Line status - 12345".
    const activity = useMemo(() => {
        const byEmail = {};
        Object.entries(cells || {}).forEach(([cell, users]) => {
            (users || []).forEach((u) => {
                if (!byEmail[u.email]) byEmail[u.email] = describeCell(cell);
            });
        });
        return byEmail;
    }, [cells]);

    const others = here.filter((u) => u.email !== me);
    if (!here.length) {
        return connected ? null : (
            <span className="presence-bar presence-bar--offline" title="Live presence is offline">
                <span className="presence-dot presence-dot--off" />
            </span>
        );
    }

    const visible = here.slice(0, MAX_VISIBLE);
    const overflow = here.length - visible.length;

    return (
        <div
            className="presence-bar"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <span
                className={`presence-dot ${connected ? 'presence-dot--on' : 'presence-dot--off'}`}
                title={connected ? 'Live' : 'Reconnecting…'}
            />
            <div className="presence-bar-stack">
                {visible.map((user) => (
                    <span
                        key={user.email}
                        className={`presence-avatar${user.email === me ? ' presence-avatar--me' : ''}`}
                        title={`${user.name}${activity[user.email] ? ` — ${activity[user.email]}` : ''}`}
                    >
                        <InitialAvatar name={user.name || user.email} size={size} useStoredAvatar={false} />
                    </span>
                ))}
                {overflow > 0 && (
                    <span className="presence-avatar presence-avatar--more" style={{ width: size, height: size }}>
                        +{overflow}
                    </span>
                )}
            </div>
            <span className="presence-bar-count">
                {others.length === 0 ? 'Only you' : `${label}: ${here.length}`}
            </span>

            {open && (
                <div className="presence-popover">
                    <div className="presence-popover-title">{label}</div>
                    <ul className="presence-popover-list">
                        {here.map((user) => (
                            <li key={user.email} className="presence-popover-item">
                                <InitialAvatar name={user.name || user.email} size={20} useStoredAvatar={false} />
                                <span className="presence-popover-name">
                                    {user.name}
                                    {user.email === me ? ' (you)' : ''}
                                </span>
                                <span className="presence-popover-activity">
                                    {activity[user.email] || 'Viewing'}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default PresenceBar;
